import { useEffect, useRef, useState } from 'react'
import { ShieldCheck, Send } from 'lucide-react'

interface AdminOtpStepProps {
  isSubmitting: boolean
  /** 재발송까지 기다려야 하는 시간(초) */
  retryAfterSec: number
  onSubmit: (code: string) => void
  onResend: () => void
  onCancel: () => void
}

const CODE_LENGTH = 6

/** 남은 시간을 1초씩 줄인다. 0 이 되면 재발송 버튼이 열린다. */
function useCountdown(initial: number): [number, (value: number) => void] {
  const [left, setLeft] = useState(initial)

  useEffect(() => {
    setLeft(initial)
  }, [initial])

  useEffect(() => {
    if (left <= 0) {
      return
    }
    const timer = setTimeout(() => setLeft(value => value - 1), 1000)
    return () => clearTimeout(timer)
  }, [left])

  return [left, setLeft]
}

/**
 * 2단계 — 텔레그램으로 받은 코드 입력.
 * 비밀번호를 통과했을 뿐 아직 로그인된 상태가 아니다.
 */
export default function AdminOtpStep({
  isSubmitting,
  retryAfterSec,
  onSubmit,
  onResend,
  onCancel,
}: AdminOtpStepProps) {
  const [code, setCode] = useState('')
  const [cooldown, setCooldown] = useCountdown(retryAfterSec)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const canSubmit = code.length === CODE_LENGTH && !isSubmitting

  return (
    <form
      className="space-y-6"
      onSubmit={event => {
        event.preventDefault()
        if (canSubmit) {
          onSubmit(code)
        }
      }}
    >
      <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/40">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" />
        <p className="text-sm text-blue-900 dark:text-blue-200">
          텔레그램으로 보낸 6자리 코드를 입력하세요. 5분 안에 유효합니다.
        </p>
      </div>

      <div>
        <label
          htmlFor="admin-otp"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          인증 코드
        </label>
        <input
          ref={inputRef}
          id="admin-otp"
          name="otp"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={CODE_LENGTH}
          value={code}
          onChange={event =>
            setCode(event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))
          }
          placeholder="000000"
          className="mt-1 block w-full rounded-lg border border-gray-300 bg-white py-3 text-center font-mono text-2xl tracking-[0.4em] text-gray-900 placeholder-gray-300 focus:border-blue-500 focus:outline-none focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        />
      </div>

      <button
        type="submit"
        disabled={!canSubmit}
        className="flex w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isSubmitting ? '확인 중…' : '로그인'}
      </button>

      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={onCancel}
          className="text-gray-500 underline transition hover:text-gray-700 dark:hover:text-gray-300"
        >
          처음부터 다시
        </button>

        <button
          type="button"
          disabled={cooldown > 0 || isSubmitting}
          onClick={() => {
            setCooldown(retryAfterSec)
            onResend()
          }}
          className="flex items-center gap-1.5 font-medium text-blue-600 transition hover:text-blue-700 disabled:cursor-not-allowed disabled:text-gray-400 dark:text-blue-400"
        >
          <Send className="h-4 w-4" />
          {cooldown > 0 ? `${cooldown}초 후 재발송` : '코드 다시 보내기'}
        </button>
      </div>
    </form>
  )
}
